const severityOrder = {
  critical: 4,
  high: 3,
  medium: 2,
  low: 1
};

const normalize = (value) => String(value || '').toLowerCase().trim();

export const buildIncidentCounts = (incidents, today = new Date()) => {
  const overdueFollowUps = incidents.filter((incident) => getFollowUpStatus(incident, today).isOverdue);
  return {
    total: incidents.length,
    open: incidents.filter((incident) => normalize(incident.status) !== 'closed').length,
    critical: incidents.filter((incident) => normalize(incident.severity) === 'critical').length,
    followUps: incidents.filter((incident) => incident.followUpRequired).length,
    overdueFollowUps: overdueFollowUps.length
  };
};

function getFollowUpStatus(incident, today = new Date()) {
  if (!incident?.followUpRequired || !incident?.followUpDate) {
    return { isOverdue: false, isDueSoon: false, daysRemaining: null };
  }
  const followUpDate = new Date(incident.followUpDate);
  if (Number.isNaN(followUpDate.getTime())) {
    return { isOverdue: false, isDueSoon: false, daysRemaining: null };
  }
  const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const diffDays = Math.ceil((followUpDate - startOfToday) / (1000 * 60 * 60 * 24));
  return {
    isOverdue: diffDays < 0 && normalize(incident.status) !== 'closed',
    isDueSoon: diffDays >= 0 && diffDays <= 7 && normalize(incident.status) !== 'closed',
    daysRemaining: diffDays
  };
}

export { getFollowUpStatus };

export const matchesIncidentSearch = ({ incident, searchTerm, participantNames, assignedName }) => {
  if (!searchTerm) return true;
  const haystack = [
    incident.type,
    incident.description,
    assignedName,
    ...participantNames
  ]
    .filter(Boolean)
    .map((value) => normalize(value))
    .join(' ');
  return haystack.includes(normalize(searchTerm));
};

export const filterIncidents = ({
  incidents,
  filters,
  participantLookup
}) => {
  return incidents.filter((incident) => {
    const participantNames =
      incident.participants?.map((participant) => participant.name || participantLookup?.[participant.id] || participant.id) || [];
    if (
      !matchesIncidentSearch({
        incident,
        searchTerm: filters.search,
        participantNames,
        assignedName: incident.assignedKeyWorker
      })
    ) {
      return false;
    }
    if (filters.types.length && !filters.types.includes(incident.type)) return false;
    if (filters.severities.length && !filters.severities.includes(normalize(incident.severity))) return false;
    if (filters.statuses.length && !filters.statuses.includes(normalize(incident.status))) return false;
    if (filters.assignment === 'assigned' && !incident.assignedKeyWorker) return false;
    if (filters.assignment === 'unassigned' && incident.assignedKeyWorker) return false;
    const followUpStatus = getFollowUpStatus(incident);
    if (filters.followUp === 'none' && incident.followUpRequired) return false;
    if (filters.followUp === 'required' && !incident.followUpRequired) return false;
    if (filters.followUp === 'dueSoon' && !followUpStatus.isDueSoon) return false;
    if (filters.followUp === 'overdue' && !followUpStatus.isOverdue) return false;
    return true;
  });
};

export const sortIncidents = (incidents, today = new Date()) => {
  return [...incidents].sort((a, b) => {
    const aCritical = normalize(a.severity) === 'critical';
    const bCritical = normalize(b.severity) === 'critical';
    if (aCritical !== bCritical) return aCritical ? -1 : 1;

    const aOverdue = getFollowUpStatus(a, today).isOverdue;
    const bOverdue = getFollowUpStatus(b, today).isOverdue;
    if (aOverdue !== bOverdue) return aOverdue ? -1 : 1;

    const aOpen = normalize(a.status) !== 'closed';
    const bOpen = normalize(b.status) !== 'closed';
    if (aOpen !== bOpen) return aOpen ? -1 : 1;

    const aDate = new Date(a.date).getTime();
    const bDate = new Date(b.date).getTime();
    if (aDate !== bDate) return bDate - aDate;

    return (severityOrder[normalize(b.severity)] || 0) - (severityOrder[normalize(a.severity)] || 0);
  });
};
